 
import styled from '@emotion/styled';
import type { ReactNode } from 'react';

interface ButtonProps {
  onClick: () => void;
  icon?: ReactNode;
  variant?: 'icon' | 'text' | 'purple' | 'transparent';
  disabled?: boolean;
  ariaLabel?: string;
  className?: string;
  children?: ReactNode;
  fullWidth?: boolean;
}

const StyledButton = styled.button<{
  variant: 'icon' | 'text' | 'purple' | 'transparent';
  disabled?: boolean;
  fullWidth?: boolean;
}>`
  border: none;
  outline: none;
  cursor: ${(props) => (props.disabled ? 'not-allowed' : 'pointer')};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: opacity 0.2s ease;
  font-family: ${(props) => props.theme.typography.fontFamily};
  ${(props) => props.fullWidth && 'width: 100%;'}

  &:focus {
    outline: none;
  }

  &:focus-visible {
    outline: none;
  }

  ${(props) => {
    if (props.variant === 'icon') {
      return `
        background: transparent;
        padding: 0;
        opacity: ${props.disabled ? 0.5 : 1};
      `;
    }
    if (props.variant === 'text') {
      return `
        background: transparent;
        padding: ${props.theme.spacing(1)};
        opacity: ${props.disabled ? 0.5 : 1};
      `;
    }
    if (props.variant === 'purple') {
      return `
        height: 34px;
        padding: 0 ${props.theme.spacing(2)};
        background: linear-gradient(to bottom, ${props.theme.colors.moradoButtonsLinearDegradado.linear.start}, ${props.theme.colors.moradoButtonsLinearDegradado.linear.end});
        border-radius: ${props.theme.spacing(1)};
        color: ${props.theme.colors.neutral.white};
        font-size: ${props.theme.typography.body3Bold.fontSize};
        font-weight: ${props.theme.typography.body3Bold.fontWeight};
        line-height: ${props.theme.typography.body3Bold.lineHeight};
        box-shadow: 0px 4px 16px 0px rgba(0, 0, 0, 0.25);
        position: relative;
        opacity: ${props.disabled ? 0.6 : 1};

        &::after {
          content: '';
          position: absolute;
          inset: 0;
          border-radius: ${props.theme.spacing(1)};
          box-shadow: inset 0px 1px 2px 0px rgba(255, 255, 255, 0.1);
          pointer-events: none;
        }

        &:hover:not(:disabled) {
          opacity: 0.9;
        }

        &:active:not(:disabled) {
          opacity: 0.8;
        }
      `;
    }
    if (props.variant === 'transparent') {
      return `
        height: 34px;
        padding: 0 ${props.theme.spacing(2)};
        background: transparent;
        border-radius: ${props.theme.spacing(1)};
        color: ${props.theme.colors.neutral.white};
        font-size: ${props.theme.typography.body3Bold.fontSize};
        font-weight: ${props.theme.typography.body3Bold.fontWeight};
        line-height: ${props.theme.typography.body3Bold.lineHeight};
        opacity: ${props.disabled ? 0.5 : 1};

        &:hover:not(:disabled) {
          opacity: 0.8;
        }

        &:active:not(:disabled) {
          opacity: 0.6;
        }
      `;
    }
    return '';
  }}
`;

export const Button = ({
  onClick,
  icon,
  variant = 'icon',
  disabled = false,
  ariaLabel,
  className,
  children,
  fullWidth = false,
}: ButtonProps) => {
  return (
    <StyledButton
      variant={variant}
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      className={className}
      fullWidth={fullWidth}
    >
      {icon}
      {children}
    </StyledButton>
  );
};
